/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useRef } from 'react'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { AnimatePresence, motion } from 'framer-motion'

import { subtitle1Css, tertiaryBodyCss } from '../../../../lib/styles/typography'
import { baseColor, secondaryTextColor } from '../../../../lib/styles/colors'
import { rippleContainerCss } from '../../../../lib/styles/cssStyles'
import { Ripple } from '../../buttons'
import useRipple from '../../../../lib/hooks/useRipple'
import { getFocusColor } from '../../../../lib/colorUtils'
import { ButtonVariant } from '../../buttons/types'

const radioButtonCss = css`
  color: ${baseColor};
  margin-top: -12px;
  border-radius: 50%;
  display: flex;
  height: 24px;
  width: 24px;
  padding: 12px;
  cursor: pointer;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  :hover,
  :focus-within {
    background-color: ${getFocusColor(baseColor, ButtonVariant.Icon)};
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
`

const radioLabelCss = css`
  display: flex;
  min-height: 20px;
`

const radioInputCss = css`
  opacity: 0;
  position: absolute;
  :hover {
    cursor: pointer;
  }
`

const descriptionCss = css`
  ${tertiaryBodyCss}
  color: ${secondaryTextColor};
`

export type RadioItem = {
  /** Description to be rendered below label. Only rendered when label is provided */
  description?: string
  /** Value represented by the RadioItem */
  value: string
  /** Label for the item. */
  label?: string
}

type RadioProps = {
  /** A11y label for the Radio */
  ariaLabel?: string
  /** Longform, optional description for the button */
  description?: string
  /** Name of the group associated with this button */
  groupName?: string
  /** Indicates whether the button is currently selected */
  isSelected: boolean
  /** Label for the button. Optional, displayed above description */
  label?: string
  /** Callback whenever the Radio is selected/de-selected */
  onSelect: () => void
  /** Value represented by this Radio */
  value: string
}

const defaultProps: Pick<RadioProps, 'ariaLabel' | 'description' | 'groupName' | 'label'> = {
  ariaLabel: undefined,
  description: undefined,
  groupName: undefined,
  label: undefined
}

const Radio = ({
  ariaLabel,
  description,
  groupName,
  isSelected,
  label,
  onSelect,
  value
}: RadioProps) => {
  const container = useRef<HTMLSpanElement>(null)
  const [ripples, focusRipple, { onTapStart, onTapEnd, onFocus, onBlur }] = useRipple(container)

  const radioCss = css`
    display: flex;
    outline: none;
    margin: 0px;
    padding: ${!description && '6px'} 12px;

    :hover {
      cursor: pointer;
    }

    p {
      margin: 0;
      :hover {
        cursor: pointer;
      }
    }
  `
  const inputId = `${value}-radio-input`

  return (
    <div css={radioCss}>
      <label css={radioLabelCss} htmlFor={inputId} id={value} title={label}>
        <motion.span
          css={radioButtonCss}
          onBlur={onBlur}
          onFocus={onFocus}
          onTap={onTapEnd}
          onTapStart={(e: MouseEvent | TouchEvent | PointerEvent) => {
            onTapStart(e)
          }}
        >
          <input
            aria-label={ariaLabel || value}
            checked={isSelected}
            css={radioInputCss}
            id={inputId}
            name={groupName}
            onChange={onSelect}
            readOnly
            type="radio"
            value={value}
          />
          <span css={rippleContainerCss} ref={container}>
            <AnimatePresence>
              {ripples?.map((r) => (
                <Ripple key={r.key} pulsate={r.pulsate} size={r.size} x={r.x} y={r.y} />
              ))}
              {focusRipple && (
                <Ripple
                  key={focusRipple.key}
                  pulsate={focusRipple.pulsate}
                  size={focusRipple.size}
                  x={focusRipple.x}
                  y={focusRipple.y}
                />
              )}
            </AnimatePresence>
          </span>
          {isSelected ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
        </motion.span>
        <div>
          <p css={subtitle1Css}>{label}</p>
          {!!description && !!label && <p css={descriptionCss}>{description}</p>}
        </div>
      </label>
    </div>
  )
}

Radio.defaultProps = defaultProps

export default Radio
